import { ChangeDetectionStrategy, Component, Input } from '@angular/core';

export interface RankedRow {
  id: string;
  name: string;
  /** Valor que define el largo de la barra */
  value: number;
  valueLabel: string;
  detail?: string;
  tag?: string;
}

/** Ranking con barra proporcional: se lee mejor que una torta o un gráfico de barras con eje. */
@Component({
  selector: 'app-ranked-list',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (rows.length === 0) {
      <p class="py-8 text-center text-sm text-gray-500 dark:text-gray-400">{{ emptyText }}</p>
    } @else {
      <ol class="space-y-3.5">
        @for (row of visibleRows; track row.id) {
          <li>
            <div class="flex items-baseline justify-between gap-3 text-sm">
              <span class="min-w-0 truncate font-medium text-gray-800 dark:text-gray-200" [title]="row.name">
                {{ row.name }}
                @if (row.tag) {
                  <span class="ml-1.5 rounded bg-brand-50 px-1.5 py-0.5 text-xs font-medium text-brand-700 dark:bg-brand-500/15 dark:text-brand-300">{{ row.tag }}</span>
                }
              </span>
              <span class="shrink-0 font-semibold text-gray-900 tabular-nums dark:text-white">{{ row.valueLabel }}</span>
            </div>
            <div class="mt-1.5 h-2 rounded-full bg-gray-100 dark:bg-gray-800" aria-hidden="true">
              <div class="h-2 rounded-full bg-brand-500" [style.width.%]="barWidth(row.value)"></div>
            </div>
            @if (row.detail) {
              <p class="mt-1 text-xs text-gray-500 tabular-nums dark:text-gray-400">{{ row.detail }}</p>
            }
          </li>
        }
      </ol>
      @if (rows.length > limit) {
        <button type="button" (click)="expanded = !expanded"
          class="mt-4 text-sm font-medium text-brand-600 hover:underline focus-visible:outline-2 focus-visible:outline-brand-500 dark:text-brand-400">
          {{ expanded ? 'Ver menos' : 'Ver los ' + rows.length }}
        </button>
      }
    }
  `,
})
export class RankedList {
  @Input() rows: RankedRow[] = [];
  @Input() limit = 6;
  @Input() emptyText = 'No hay datos en estas fechas.';
  expanded = false;

  get visibleRows(): RankedRow[] {
    return this.expanded ? this.rows : this.rows.slice(0, this.limit);
  }

  barWidth(value: number): number {
    const max = Math.max(...this.rows.map((r) => r.value), 0);
    if (max <= 0 || value <= 0) return 0;
    // Mínimo visible para valores muy pequeños
    return Math.max(2, (value / max) * 100);
  }
}
